import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { SubheaderText, Title } from '../styledComponents';
import BookingSummary from '../../bookConsultation/BookingSummary/BookingSummary';
import styled from '@emotion/native';
import { green, red, subheadline } from '@/shared/styles/colors';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { formatCurrency, getDropdownOptionLabel } from '@/shared/utils';
import { BaseText, BodyText } from '@/shared/components/StyledText';
import { useImageRatio } from '@/shared/hooks/useImageRatio';
import useDimensions from '@/shared/hooks/useDimensions';
import { PRICING_TIER_OPTIONS } from '@/shared/constants/instantCall';
import StylistProfileModal from '../../stylist-profile/StylistProfileModal';
import { useShallow } from 'zustand/react/shallow';

const MATCH_EXPIRATION_TIME_SECONDS = 120;

interface Props {
  onTimerExpire: () => void;
}

const ClientMatchFound = ({ onTimerExpire }: Props) => {
  const { width } = useDimensions();

  const { matchedStylist, matchStatus, requestForm } = useInstantStylingStore(
    useShallow((state) => ({
      matchedStylist: state.matchedStylist,
      matchStatus: state.matchStatus,
      requestForm: state.requestForm,
    })),
  );

  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const [timeLeft, setTimeLeft] = useState(MATCH_EXPIRATION_TIME_SECONDS);

  const formattedTime = `${Math.floor(timeLeft / 60)}:${String(
    timeLeft % 60,
  ).padStart(2, '0')}`;

  const imageContentWidth = width - 24 * 2; // add horizontal padding
  const profilePicRatio = useImageRatio(
    matchedStylist?.user?.profilePicUrl ?? '',
  );

  const isStylistNotified = matchStatus?.clientAccepted ?? false;

  const priceTierName = getDropdownOptionLabel(
    matchedStylist?.pricing?.tierName ?? '',
    PRICING_TIER_OPTIONS,
  );

  const onPressViewProfile = () => {
    setIsProfileModalVisible(true);
  };

  // Tick the timer down
  useEffect(() => {
    const id = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (timeLeft === 0 && !isStylistNotified) {
      onTimerExpire();
    }
  }, [timeLeft, onTimerExpire, isStylistNotified]);

  return (
    <FindingMatchContainer>
      <TopContentContainer>
        <Title>{isStylistNotified ? 'Get Ready' : 'Stylist Found'}</Title>
        {isStylistNotified ? (
          <SubheaderText>
            Your stylist has been notified. Join button will show once stylist
            enters the call.
          </SubheaderText>
        ) : (
          <SubheaderText>
            You have a total of <MinutesText>{formattedTime} mins</MinutesText>
            {`\n`}to accept before the match expires.
          </SubheaderText>
        )}
      </TopContentContainer>
      <ProfileImageContainer width={imageContentWidth}>
        {imageLoading && (
          <LoadingPlaceholder>
            <ActivityIndicator size="large" color={green} />
          </LoadingPlaceholder>
        )}
        <ProfileImage
          source={{ uri: matchedStylist?.user?.profilePicUrl }}
          style={{ aspectRatio: profilePicRatio }}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />
      </ProfileImageContainer>
      <PricingProfileRow>
        <PricingContainer>
          <PricingText>
            {matchedStylist?.pricing?.hourlyRate
              ? formatCurrency(matchedStylist.pricing.hourlyRate)
              : 'XXX'}
            /hr
          </PricingText>
          <PricingTier>{priceTierName}</PricingTier>
        </PricingContainer>
        <ViewProfileLink onPress={onPressViewProfile}>
          View Profile
        </ViewProfileLink>
      </PricingProfileRow>

      <BookingSummary
        isInstant
        priceTierName={priceTierName}
        hourlyRate={matchedStylist?.pricing?.hourlyRate}
        initialDiscountCode={requestForm.discountCode}
        initialDiscountPercentage={requestForm.discountPercentage}
        isPromoCodeLocked
      />

      {/* // TODO (ES-465): Add/fix back to top button */}

      <StylistProfileModal
        id={matchedStylist?.id ?? null}
        isVisible={isProfileModalVisible}
        setIsVisible={setIsProfileModalVisible}
      />
    </FindingMatchContainer>
  );
};

export default ClientMatchFound;

const FindingMatchContainer = styled.View({
  flex: 1,
  width: '100%',
  paddingBottom: 56,
});

const TopContentContainer = styled.View({
  paddingHorizontal: 24,
});

const MinutesText = styled(SubheaderText)({
  fontWeight: 'bold',
  color: red,
});

const ProfileImageContainer = styled.View(({ width }: { width: number }) => ({
  width,
  borderRadius: 10,
  justifyContent: 'center',
  alignItems: 'center',
  marginHorizontal: 24,
  marginVertical: 16,
  overflow: 'hidden',
  position: 'relative',
}));

const LoadingPlaceholder = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f5f5f5',
  zIndex: 1,
});

const ProfileImage = styled.Image({
  width: '100%',
});

const PricingProfileRow = styled.View({
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  paddingHorizontal: 32,
  marginBottom: 36,
});

const PricingContainer = styled.View({});

const PricingText = styled(BodyText)({
  fontSize: 20,
  color: green,
});

const PricingTier = styled(BaseText)({
  fontSize: 12,
  color: subheadline,
});

const ViewProfileLink = styled(BodyText)({
  fontWeight: 'bold',
  color: green,
  textDecorationLine: 'underline',
});
